import { isVideoFile } from './constants';

export type MediaProbeResult = {
  width: number;
  height: number;
  duration?: number;
  format: string | undefined;
  thumbnailUrl?: string;
};

const PROBE_TIMEOUT_MS = 4_000;
const THUMBNAIL_MAX_WIDTH = 240;
const PLAYABLE_VIDEO_FORMATS = new Set(['mp4', 'webm', 'ogg']);

export class MediaDimensionsProbe {
  static probe(file: File): Promise<MediaProbeResult> {
    return new MediaDimensionsProbe(file).run();
  }

  private static captureVideoFrame(video: HTMLVideoElement): string | undefined {
    const scale = Math.min(1, THUMBNAIL_MAX_WIDTH / video.videoWidth);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return undefined;
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.6);
  }

  private constructor(private readonly file: File) {}

  private async run(): Promise<MediaProbeResult> {
    const probeUrl = URL.createObjectURL(this.file);
    try {
      if (this.file.type.startsWith('image/')) {
        return await this.image(probeUrl);
      }
      if (this.file.type.startsWith('video/') || isVideoFile(this.file)) {
        return await this.video(probeUrl);
      }
      throw new Error(`Unsupported file type for media probe: ${this.file.type}`);
    } finally {
      URL.revokeObjectURL(probeUrl);
    }
  }

  private image(probeUrl: string): Promise<MediaProbeResult> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const timer = setTimeout(() => reject(new Error('image probe timed out')), PROBE_TIMEOUT_MS);
      img.onload = () => {
        clearTimeout(timer);
        resolve({ width: img.naturalWidth, height: img.naturalHeight, format: undefined });
      };
      img.onerror = (err) => {
        clearTimeout(timer);
        reject(err);
      };
      img.src = probeUrl;
    });
  }

  private video(probeUrl: string): Promise<MediaProbeResult> {
    const rawFormat = this.file.type.split('/')[1] ?? this.file.type;
    const format = PLAYABLE_VIDEO_FORMATS.has(rawFormat) ? rawFormat : 'mp4';
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';

      const timer = setTimeout(() => reject(new Error('video probe timed out')), PROBE_TIMEOUT_MS);

      const finish = (result: MediaProbeResult) => {
        clearTimeout(timer);
        resolve(result);
      };

      const fail = (err: unknown) => {
        clearTimeout(timer);
        reject(err instanceof Error ? err : new Error('video probe failed'));
      };

      video.addEventListener('loadeddata', () => {
        const dims = { width: video.videoWidth, height: video.videoHeight, duration: video.duration, format };
        try {
          const thumbnailUrl = MediaDimensionsProbe.captureVideoFrame(video);
          finish({ ...dims, thumbnailUrl });
        } catch {
          // canvas.toDataURL throws on tainted canvases (cross-origin src) — resolve without thumbnail
          finish(dims);
        }
      });
      video.addEventListener('error', () => fail(new Error('video probe failed')));
      video.src = probeUrl;
    });
  }
}
